import { Component, Input, forwardRef, OnInit, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { CustomSelectComponent } from '../custom-select/custom-select.component';

interface Opt { label: string; value: any; id?: string; }

@Component({
  standalone: true,
  selector: 'app-quick-add-select',
  imports: [CommonModule, FormsModule, CustomSelectComponent],
  templateUrl: './quick-add-select.component.html',
  styleUrls: ['./quick-add-select.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => QuickAddSelectComponent), multi: true }],
})
export class QuickAddSelectComponent implements ControlValueAccessor, OnInit {
  private destroyRef = inject(DestroyRef);
  private static readonly MANAGE_ROLES = ['hr', 'hr_admin', 'admin', 'super_admin'];

  @Input() category = '';
  @Input() placeholder = 'Select';
  @Input() allowAdd = true;
  @Input() disabled = false;
  @Input() seedOptions: Opt[] = [];

  options: Opt[] = [];
  value: any = null;

  modalOpen = false;
  newValue = '';
  saving = false;
  busyId: string | null = null;

  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private api: ApiService, private auth: AuthService, private snackbar: SnackbarService) {}

  get canManage(): boolean {
    const r = (this.auth.getCurrentUser()?.role || '').toLowerCase().replace(/^role_/, '');
    return QuickAddSelectComponent.MANAGE_ROLES.includes(r);
  }
  get showAdd(): boolean { return this.allowAdd && this.canManage && !!this.category; }

  ngOnInit(): void {
    this.options = [...this.seedOptions];
    if (this.category) this.loadOptions();
  }

  private merge(picklist: Opt[]): void {
    const seen = new Set<string>();
    const out: Opt[] = [];
    for (const o of [...this.seedOptions, ...picklist]) {
      const key = String(o.value).toLowerCase();
      if (!seen.has(key)) { seen.add(key); out.push(o); }
    }
    this.options = out;
  }

  private loadOptions(select?: string): void {
    this.api.get('/picklists/' + this.category).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        this.merge(list.map((o: any) => ({ label: o.label || o.value, value: o.value, id: o.id })));
        if (select) { this.value = select; this.onChange(select); }
      },
      error: () => { this.merge([]); },
    });
  }

  openModal(): void {
    if (this.disabled || !this.canManage) return;
    this.newValue = '';
    this.modalOpen = true;
  }
  closeModal(): void { this.modalOpen = false; }

  saveNew(): void {
    const val = (this.newValue || '').trim();
    if (!val) { this.snackbar.showFailure('Enter a value'); return; }
    this.saving = true;
    this.api.post('/picklists', { category: this.category, value: val }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving = false;
        this.modalOpen = false;
        this.loadOptions(val);
        this.snackbar.showSuccess('Option added');
      },
      error: () => { this.saving = false; this.snackbar.showFailure('Could not add option'); },
    });
  }

  deleteOption(o: Opt, ev: Event): void {
    ev.stopPropagation();
    if (!o.id || !this.canManage) return;
    this.busyId = o.id;
    this.api.delete('/picklists/' + o.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.busyId = null;
        if (this.value === o.value) { this.value = null; this.onChange(null); }
        this.loadOptions();
        this.snackbar.showSuccess('Option removed');
      },
      error: () => { this.busyId = null; this.snackbar.showFailure('Could not remove option'); },
    });
  }

  onSelectChange(v: any): void { this.value = v; this.onChange(v); this.onTouched(); }

  writeValue(v: any): void { this.value = v; }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.disabled = d; }
}
